/**
 * Notifications are stored as a type plus values and rendered in the
 * reader's language; the stored English text is only a fallback.
 */
import type { TFunction } from "i18next";
import { formatDate } from "../features/shared.js";

export interface NotificationRecord {
  type: string;
  params?: Record<string, string> | null;
  title: string;
  body?: string | null;
  entityType?: string | null;
  entityId?: string | null;
}

const DATE_PARAMS = new Set(["week", "dueDate"]);

export function notificationText(
  notification: NotificationRecord,
  t: TFunction,
): { title: string; body: string } {
  const values: Record<string, string> = {};
  for (const [key, value] of Object.entries(notification.params ?? {}))
    values[key] = DATE_PARAMS.has(key) ? formatDate(value) : value;
  const key = `notification.${notification.type}`;
  return {
    title: t(`${key}.title`, {
      ...values,
      defaultValue: notification.title,
    }),
    body: t(`${key}.body`, {
      ...values,
      defaultValue: notification.body ?? "",
    }),
  };
}

/** Where a notification leads when opened. */
export function notificationHref(
  notification: NotificationRecord,
): string | null {
  const id = notification.entityId;
  switch (notification.entityType) {
    case "request":
      return id ? `/requests/${id}` : null;
    case "invoice":
      return id ? `/invoices/${id}` : null;
    case "expense_claim":
      return notification.type === "expense_claim_submitted"
        ? "/expenses?status=submitted"
        : "/expenses";
    case "timesheet": {
      const week = notification.params?.week;
      const query = week ? `?week=${week}` : "";
      return notification.type === "timesheet_submitted"
        ? `/time/approvals${query}`
        : `/time/week${query}`;
    }
    default:
      return null;
  }
}
